import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useExternalPricing } from "@/hooks/useExternalPricing";
import { PublicPlanSelection } from "@/components/subscriptions/PublicPlanSelection";
import { LoaderCircle, ShieldAlert, Check, Facebook, Instagram, Youtube } from "lucide-react";
import { Button } from "@/components/ui/button";
import { renderHTML } from "@/components/subscriptions/utils";
import { supabase } from "@/integrations/supabase/client";

const PublicProductSubscription = () => {
  const [searchParams] = useSearchParams();
  const productName = searchParams.get('name');
  const schoolId = searchParams.get('schoolid');
  const userId = searchParams.get('userid');
  const token = searchParams.get('token');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fetchAttempted, setFetchAttempted] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const {
    loading: pricingLoading,
    pricingData,
    error: pricingError,
    fetchPricingPlans
  } = useExternalPricing();
  
  useEffect(() => {
    const validateAccessAndFetchData = async () => {
      try {
        // Only run this once to prevent infinite loop
        if (fetchAttempted) return;
        setLoading(true);
        setFetchAttempted(true);

        // Check required parameters - name and schoolId are mandatory
        if (!productName || !schoolId) {
          setError("Missing required parameters: product name and school ID");
          toast({
            variant: "destructive",
            title: "Error",
            description: "Missing required parameters"
          });
          setLoading(false);
          return;
        }

        // Use the edge function to validate token and get subscriptions
        // This bypasses RLS restrictions since edge functions can use the service role
        console.log("Fetching subscriptions with params:", 
          { schoolId, token: token || 'none', productName });
        
        // Fixed: Pass parameters in the body instead of query
        const { data: validationData, error: validationError } = await supabase.functions
          .invoke('get-school-subscriptions', {
            body: { 
              schoolId,
              ...(token && { token })
            }
          });

        console.log("Edge function response:", validationData, validationError);

        if (validationError || (validationData && !validationData.success)) {
          const errorMessage = validationError?.message || 
            (validationData?.error || "Access validation failed");
          
          console.error("Validation error:", errorMessage);
          setError(errorMessage);
          setLoading(false);
          return;
        }
        
        // After token validation passes, fetch pricing plans
        console.log("Fetching pricing plans for:", productName, "school ID:", schoolId);
        await fetchPricingPlans(schoolId, productName);
        setLoading(false);
      } catch (err: any) {
        console.error('Error in PublicProductSubscription:', err);
        setError(err.message || "An unexpected error occurred");
        setLoading(false);
      }
    };
    
    validateAccessAndFetchData();
  }, [productName, schoolId, userId, token, toast, fetchPricingPlans, fetchAttempted]);

  if (loading || pricingLoading) {
    return <div className="min-h-screen w-full flex items-center justify-center bg-[#F0F7F6]">
        <LoaderCircle className="h-8 w-8 animate-spin text-[#008169]" />
      </div>;
  }
  
  if (error) {
    return <div className="min-h-screen w-full flex items-center justify-center bg-[#F0F7F6]">
        <div className="bg-white p-8 rounded-lg shadow-md text-center max-w-md">
          <div className="flex justify-center mb-4">
            <ShieldAlert className="h-12 w-12 text-red-500" />
          </div>
          <h2 className="text-2xl font-semibold text-red-600 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <p className="text-gray-500 mb-6 text-sm">
            Please contact the school administrator to get a valid access link.
          </p>
          <Button onClick={() => navigate('/')} className="w-full">
            Return to Homepage
          </Button>
        </div>
      </div>;
  }
  
  if (pricingError) {
    return <div className="min-h-screen w-full flex items-center justify-center bg-[#F0F7F6]">
        <div className="bg-white p-8 rounded-lg shadow-md text-center max-w-md">
          <div className="flex justify-center mb-4">
            <ShieldAlert className="h-12 w-12 text-red-500" />
          </div>
          <h2 className="text-2xl font-semibold text-red-600 mb-2">Error</h2>
          <p className="text-gray-600 mb-4">{pricingError}</p>
          <Button onClick={() => navigate('/')} className="w-full">
            Return to Homepage
          </Button>
        </div>
      </div>;
  }
  
  if (!pricingData) {
    return <div className="min-h-screen w-full flex items-center justify-center bg-[#F0F7F6]">
        <div className="bg-white p-8 rounded-lg shadow-md text-center max-w-md">
          <h2 className="text-2xl font-semibold mb-2">No plans available</h2>
          <p className="text-gray-600 mb-4">
            No pricing information found for this product.
          </p>
          <Button onClick={() => navigate('/')} className="w-full">
            Return to Homepage
          </Button>
        </div>
      </div>;
  }

  const showTokenWarning = !token && !loading && !error && pricingData;

  return <div className="min-h-screen bg-[#F0F7F6]">
      <header className=" py-4">
        <div className="container mx-auto px-4 md:px-8 flex justify-between items-center">
          <div className="text-2xl font-bold text-[#008169]">
            <div className="flex items-center gap-2">
              
            </div>
          </div>
          <img src="https://idn-static-assets.s3.ap-southeast-1.amazonaws.com/subscription/infra_logo.png" alt="Infra Digital Logo" className="h-10" />
        </div>
      </header>

      {showTokenWarning && <div className="container mx-auto px-4 md:px-8 py-4">
          <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800">
            <h3 className="flex items-center text-lg font-semibold">
              <ShieldAlert className="h-5 w-5 mr-2" />
              Security Notice
            </h3>
            <p className="mt-1">
              You're accessing this page without a security token. For increased security, 
              please request a secure subscription link from your administrator.
            </p>
          </div>
        </div>}

      <section className="container mx-auto px-4 md:px-8 py-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="flex justify-center lg:justify-start">
            <img src={pricingData.product.product_screen || "/placeholder.svg"} alt={pricingData.product.name} className="max-w-full h-auto rounded-lg" />
          </div>
          
          <div>
            <PublicPlanSelection pricingData={pricingData} schoolId={schoolId} userId={userId} productName={productName} token={token} />
          </div>
        </div>
      </section>

      <section className="bg-[#E1F0ED] py-12">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h3 className="text-2xl font-semibold mb-4 text-gray-800">{pricingData.product.name}</h3>
              {pricingData.product.description && <div className="prose max-w-none text-gray-700" dangerouslySetInnerHTML={renderHTML(pricingData.product.description)} />}
            </div>
            
            <div>
              <h3 className="text-xl font-semibold mb-4 text-gray-800">Features</h3>
              {pricingData.product.features && <div className="space-y-3">
                  {pricingData.product.features.split('\n').map((feature, index) => <div key={index} className="flex items-start">
                      <Check className="h-5 w-5 text-[#008169] mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{feature.replace(/^[•-]\s*/, '')}</span>
                    </div>)}
                </div>}
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-white py-8 shadow-inner">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <p className="text-gray-600">© 2025 {pricingData.product.name}. All rights reserved.</p>
            </div>
            <div className="flex gap-6">
              <a href="#" className="text-gray-600 hover:text-[#008169]">
                <Facebook className="h-6 w-6" />
              </a>
              <a href="#" className="text-gray-600 hover:text-[#008169]">
                <Instagram className="h-6 w-6" />
              </a>
              <a href="#" className="text-gray-600 hover:text-[#008169]">
                <Youtube className="h-6 w-6" />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>;
};

export default PublicProductSubscription;
